import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DesktopLayout from '../components/layout/DesktopLayout';
import AdCardList from '../components/advertising/AdCardList';
import { getFeaturedAds } from '../services/adsApi';
import type { Ad } from '../types';
import './HomePage.css';

/**
 * 首頁
 * 顯示精選廣告和主要功能入口
 */
const HomePage = () => {
  const navigate = useNavigate();
  const [featuredAds, setFeaturedAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);

  // 取得精選廣告
  useEffect(() => {
    const fetchFeaturedAds = async () => {
      setLoading(true);
      try {
        const ads = await getFeaturedAds(12, 1); // 取得精選等級1的廣告，最多12個作為緩衝
        const desktopFeaturedAds = (ads as Ad[]).slice(0, 6);
        setFeaturedAds(desktopFeaturedAds);
      } catch (error) {
        console.error('Failed to fetch featured ads:', error);
        setFeaturedAds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedAds();
  }, []);

  const handleAdClick = (adId: string) => {
    navigate(`/ads/${adId}`);
  };

  return (
    <div className="home-page">
      <DesktopLayout>
        <div className="home-page__container">
          {/* Hero Section */}
          <section className="home-page__hero">
            <div className="home-page__hero-content">
              <h1 className="home-page__hero-title">瑋瑋車業</h1>
              <p className="home-page__hero-subtitle">
                專業汽車服務媒合平台，找到最適合您的車業服務
              </p>
              <div className="home-page__hero-actions">
                <button
                  className="btn btn--primary btn--large"
                  onClick={() => navigate('/ads')}
                >
                  汽車地圖
                </button>
                <button
                  className="btn btn--secondary btn--large"
                  onClick={() => navigate('/wishes')}
                >
                  我要許願
                </button>
              </div>
            </div>
          </section>

          {/* Brand Philosophy Section */}
          <section className="home-page__brand-philosophy">
            <div className="brand-philosophy__content">
              <div className="brand-philosophy__header">
                <h2 className="brand-philosophy__title">打造全網最透明線上車庫</h2>
              </div>

              {/* Brand Image */}
              <div className="brand-image-section">
                <img src="/weiwei_brand.jpg" alt="瑋瑋車業品牌" className="brand-image" />
              </div>

              <div className="philosophy-content">
                <div className="philosophy-section">
                  <h3 className="philosophy-section__title">瑋瑋中古車致力打造全台二手車透明化</h3>
                  <div className="philosophy-section__content">
                    <p>瑋瑋中古車創立於 2023 年，是一家基於自媒體平臺的二手車買賣創新型企業，致力打造全台灣最公開的二手車銷售平台。</p>
                    <p>總部設立於水最深的桃園，為了抵制詐騙風氣，是二手車買賣行業最具有代表性的領頭羊。</p>
                    <div className="philosophy-stats">
                      <div className="stat-item">
                        <span className="stat-number">7億+</span>
                        <span className="stat-label">全網覆蓋流量</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">700+</span>
                        <span className="stat-label">合作車廠</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">3萬+</span>
                        <span className="stat-label">年服務用戶</span>
                      </div>
                    </div>
                    <p>其影響力、服務及品質被眾多網紅大咖認可，以服務理念及優質的車源為客戶高效提供好品質、好價格的源頭貨，一站式的購車體驗。</p>
                  </div>
                </div>
              </div>

              {/* Full Width Service Banner */}
              <div className="philosophy-banner">
                <h3 className="philosophy-banner__title">不只賣車 更重視行車安全</h3>
                <p className="philosophy-banner__text">全台灣不管你在哪個縣市，你點一下就會有人照顧你</p>
                <div className="service-list">
                  <span className="service-tag">保養</span>
                  <span className="service-tag">維修</span>
                  <span className="service-tag">包膜</span>
                  <span className="service-tag">美容</span>
                  <span className="service-tag">購車</span>
                </div>
                <p className="philosophy-banner__summary">瑋瑋全包</p>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="home-page__features">
            <div className="feature-card" onClick={() => navigate('/ads')}>
              <div className="feature-card__icon">🔍</div>
              <h3 className="feature-card__title">汽車地圖</h3>
              <p className="feature-card__description">
                依地區和服務項目快速找到您需要的車業服務
              </p>
            </div>

            <div className="feature-card" onClick={() => navigate('/wishes')}>
              <div className="feature-card__icon">💡</div>
              <h3 className="feature-card__title">我要許願</h3>
              <p className="feature-card__description">
                找不到合適的服務？在許願池許下願望，讓服務商主動聯繫您
              </p>
            </div>

            <div className="feature-card" onClick={() => navigate('/member')}>
              <div className="feature-card__icon">📊</div>
              <h3 className="feature-card__title">會員中心</h3>
              <p className="feature-card__description">
                管理您的廣告、查看統計資料、設定通知偏好
              </p>
            </div>
          </section>

          {/* Featured Ads Section */}
          {(!loading || featuredAds.length > 0) && (
            <section className="home-page__featured">
              <h2 className="home-page__section-title">精選廣告</h2>
              <AdCardList ads={featuredAds} loading={loading} onAdClick={handleAdClick} />
              <div className="home-page__view-all">
                <button
                  className="btn btn--secondary"
                  onClick={() => navigate('/ads')}
                >
                  查看所有廣告
                </button>
              </div>
            </section>
          )}
        </div>

        <footer className="home-page__footer">
          <div className="home-page__footer-decoration"></div>
          <div className="home-page__footer-content">
            <div className="home-page__footer-section">
              <h4>關於瑋瑋車業</h4>
              <p>瑋瑋中古車創立於 2023 年，致力打造全台灣最公開、最透明的二手車銷售平台。總部設立於桃園，以抵制詐騙風氣為使命，成為二手車買賣行業的領頭羊。</p>
            </div>
            <div className="home-page__footer-section">
              <h4>服務項目</h4>
              <ul className="service-list-compact">
                <li>汽車地圖框架</li>
                <li>車輛基礎保養</li>
                <li>租車享額外優惠</li>
                <li>汽車洗車美容</li>
                <li>道路救援拖吊</li>
                <li>車輛外觀改裝</li>
                <li>貼膜服務</li>
                <li>車輛影音改裝</li>
                <li>輪胎服務</li>
                <li>定位校正</li>
                <li>車輛繳費系統</li>
                <li>保險理賠</li>
                <li>驗車提醒</li>
                <li>保險提醒</li>
                <li>油車電車分開</li>
                <li>充電地圖</li>
                <li>線上估車</li>
                <li>車輛故障碼指南</li>
                <li>汽車周邊購物</li>
                <li>會員等級</li>
                <li>圖友分享</li>
                <li>影音專區</li>
              </ul>
            </div>
            <div className="home-page__footer-section">
              <h4>聯絡我們</h4>
              <p>Email: info@weiweicar.com</p>
              <p>電話: 0800-123-456</p>
            </div>
          </div>
        </footer>
      </DesktopLayout>
    </div>
  );
};

export default HomePage;
